(function (root, factory) {
  'use strict';
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.NikuyaCartAdapter = factory();
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const validId = value => (typeof value === 'string' && /^[1-9]\d*$/.test(value)) ||
    (Number.isSafeInteger(value) && value > 0);
  const integer = value => Number.isSafeInteger(value) && value >= 0;
  function failure(code) { return Object.assign(new Error(code), { code }); }

  function create(options = {}) {
    if (options.enabled !== true) {
      return Object.freeze({
        read: async () => { throw failure('ADAPTER_DISABLED'); },
        mutate: async () => { throw failure('ADAPTER_DISABLED'); }
      });
    }
    const { fetch: transport, origin, routesRoot, allowedVariantIds = [], timeoutMs = 5000 } = options;
    if (typeof transport !== 'function' || typeof origin !== 'string' ||
        typeof routesRoot !== 'string' || !/^\/(?:[a-zA-Z0-9_-]+\/)*$/.test(routesRoot) ||
        !Number.isSafeInteger(timeoutMs) || timeoutMs < 1 || timeoutMs > 30000 ||
        !Array.isArray(allowedVariantIds) || !allowedVariantIds.every(validId)) {
      throw failure('INVALID_ADAPTER_CONFIGURATION');
    }
    const site = new URL(origin);
    if (!['http:', 'https:'].includes(site.protocol) || site.origin !== origin) {
      throw failure('INVALID_ADAPTER_ORIGIN');
    }
    const variants = new Set(allowedVariantIds.map(String));
    async function request(endpoint, body) {
      const controller = new AbortController();
      let timer;
      const deadline = new Promise((_, reject) => {
        timer = setTimeout(() => {
          reject(failure('CART_TRANSPORT_TIMEOUT'));
          controller.abort();
        }, timeoutMs);
      });
      try {
        return await Promise.race([deadline, (async () => {
          const response = await transport(new URL(routesRoot + endpoint, site).href, {
            method: body ? 'POST' : 'GET', credentials: 'same-origin',
            cache: 'no-store', redirect: 'error', signal: controller.signal,
            headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
            ...(body ? { body: JSON.stringify(body) } : {})
          });
          if (!response || response.ok !== true) throw failure('CART_HTTP_ERROR');
          const data = await response.json();
          if (!data || typeof data !== 'object' || Array.isArray(data) || data.status >= 400) {
            throw failure('INVALID_CART_RESPONSE');
          }
          return data;
        })()]);
      } finally { clearTimeout(timer); }
    }
    return Object.freeze({
      async read() {
        const cart = await request('cart.js');
        if (!Array.isArray(cart.items) || !integer(cart.item_count) || !integer(cart.total_price) ||
            typeof cart.currency !== 'string' || !/^[A-Z]{3}$/.test(cart.currency)) {
          throw failure('INVALID_CART_RESPONSE');
        }
        const keys = new Set();
        let count = 0;
        for (const line of cart.items) {
          if (!line || typeof line.key !== 'string' || !line.key || keys.has(line.key) ||
              !validId(line.product_id) || !validId(line.variant_id) ||
              !integer(line.quantity) || line.quantity === 0) throw failure('INVALID_CART_RESPONSE');
          keys.add(line.key);
          count += line.quantity;
        }
        if (!integer(count) || count !== cart.item_count) throw failure('INVALID_CART_RESPONSE');
        return cart;
      },
      async mutate(action) {
        if (!action || action.type !== 'recommendation' || !validId(action.variantId) ||
            !variants.has(String(action.variantId)) ||
            Object.keys(action).some(key => !['type', 'variantId'].includes(key))) {
          throw failure('UNSUPPORTED_CART_ACTION');
        }
        return request('cart/add.js', { items: [{ id: String(action.variantId), quantity: 1 }] });
      }
    });
  }
  /** Future wire contract only. Never passed to the existing paid recommendation transport. */
  function prepareCaseMutation(plan, caseSku, gates = {}) {
    const state = plan?.version === 2 && plan.phase === 'F1B2A_CONTRACT_ONLY' &&
      typeof plan.snapshotRevision === 'string' && plan.cases?.find(item => item.caseSku === caseSku);
    if (!state || state.blockers.length) return { status: 'BLOCKED', code: 'CASE_PLAN_BLOCKED', request: null };
    const action = state.actions.find(item => item.type !== 'NO_OP');
    const cleanup = action && ['DECREASE_MANAGED_CASE', 'REMOVE_MANAGED_CASE'].includes(action.type);
    const required = cleanup ? ['caseReconciliationEnabled', 'exactMappingVerified', 'acquisitionAuthorityValid'] :
      ['caseReconciliationEnabled', 'financialEnforcementVerified', 'casePricingVerified', 'exactMappingVerified', 'exactCaseAvailable', 'acquisitionAuthorityValid'];
    const closed = required.filter(key => gates?.[key] !== true);
    if (closed.length) return { status: 'BLOCKED', code: 'CASE_MUTATION_GATES_CLOSED', closed, request: null };
    if (!action) return { status: 'NO_OP', request: null };
    if (cleanup) {
      const line = state.managedLines?.find(l => l.key === action.key);
      if (!state.cleanupOnly || !line || action.quantity < 0 || action.quantity >= line.quantity ||
          line.quantity - action.quantity > state.currentManagedQuantity - state.desiredManagedQuantity) {
        return { status: 'BLOCKED', code: 'INVALID_CLEANUP_ACTION', request: null };
      }
    }
    if (!integer(action.quantity) || !validId(action.variantId)) return { status: 'BLOCKED', code: 'INVALID_CASE_ACTION', request: null };
    let request;
    if (action.type === 'ADD_MANAGED_CASE' && action.quantity > 0 && action.properties?._nikuya_reward === 'nikuya-acquisition-case-v2') {
      request = { endpoint: 'cart/add.js', body: { items: [{ id: action.variantId,
        quantity: action.quantity, properties: { ...action.properties } }] } };
    } else if (['INCREASE_MANAGED_CASE', 'DECREASE_MANAGED_CASE', 'REMOVE_MANAGED_CASE'].includes(action.type) &&
        typeof action.key === 'string' && state.managedKeys.includes(action.key)) {
      request = { endpoint: 'cart/change.js', body: { id: action.key, quantity: action.quantity } };
    } else return { status: 'BLOCKED', code: 'INVALID_CASE_ACTION', request: null };
    // One operation per fresh snapshot: a write can change keys, discounts and availability.
    return { status: 'PREPARED_NOT_EXECUTED', snapshotRevision: plan.snapshotRevision, request,
      requiresFreshReadAfterWrite: true, retryAutomatically: false };
  }
  async function executeCaseReconciliation() {
    // No transport injection or runtime option can enable reward writes in this checkpoint.
    return { status: 'BLOCKED', code: 'REAL_CASE_EXECUTION_DISABLED_F1B2A', writes: 0,
      confirmed: false, requiresAuthoritativeRead: true };
  }
  return Object.freeze({ create, prepareCaseMutation, executeCaseReconciliation });
});
